import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  useTheme,
  alpha,
  TextField,
  Button,
} from '@mui/material';
import {
  Notes as NotesIcon,
  History as HistoryIcon,
  Timeline as TimelineIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import type { Execution, ExecutionLogEntry } from '../../types/attacks';
import { ExecutionStatusColors } from '../../types/attacks';
import { useTimezone } from '../../contexts/TimezoneContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTenant } from '../../contexts/TenantContext';
import { formatUiDateTime, formatUiTimeWithSeconds } from '../../utils/dateUtils';
import { describeResourceCaps, hasResourceCaps } from '../../utils/resourceCaps';
import MetricsPanel from '../operations/MetricsPanel';
import { useExecutionCursorSync } from '../../hooks/useExecutionCursorSync';
import api from '../../services/api';

const SPLIT_PCT_KEY = 'runbooks.execDetail.logMetricsSplitPct.v1';
const SHOW_SUMMARY_KEY = 'runbooks.execDetail.showSummary.v1';
const SHOW_LOG_KEY = 'runbooks.execDetail.showLog.v1';
const SHOW_METRICS_KEY = 'runbooks.execDetail.showMetrics.v1';
const DIVIDER_PX = 10;

function readStoredSplitPct(): number {
  try {
    const v = localStorage.getItem(SPLIT_PCT_KEY);
    const n = v ? parseFloat(v) : NaN;
    if (Number.isFinite(n) && n >= 20 && n <= 80) return n;
  } catch { /* ignore */ }
  return 38;
}

function readStoredBool(key: string, def: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch { /* ignore */ }
  return def;
}

function durationMs(start?: string, end?: string): number | null {
  if (!start || !end) return null;
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return b - a;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatLogEventType(eventType: string): string {
  const map: Record<string, string> = {
    activate: 'Activate scenario',
    start: 'Start attack',
    start_failed: 'Start failed',
    pause: 'Pause',
    termination: 'Termination',
    scale: 'Scale',
    config_change: 'Config change',
  };
  return map[eventType] ?? eventType;
}

function userKeyHue(key: string): number {
  let h = 0;
  const s = key || '';
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

function userAvatarColors(userKey: string, isDark: boolean): { bg: string; fg: string } {
  const hue = userKeyHue(userKey);
  if (isDark) return { bg: `hsl(${hue} 58% 36%)`, fg: `hsl(${hue} 15% 96%)` };
  return { bg: `hsl(${hue} 52% 44%)`, fg: `hsl(${hue} 10% 99%)` };
}

function userInitialFromEmail(email: string | null | undefined): string {
  if (!email?.trim()) return '';
  const local = email.trim().split('@')[0] || email.trim();
  const c = local.charAt(0);
  return c ? c.toUpperCase() : '';
}

function logUserInitial(entry: ExecutionLogEntry): string {
  const fromEmail = userInitialFromEmail(entry.user_email);
  if (fromEmail) return fromEmail;
  const id = entry.user_id?.trim();
  if (id) { const c = id.charAt(0); return /[a-zA-Z0-9]/.test(c) ? c.toUpperCase() : '?'; }
  return '?';
}

function logUserKey(entry: ExecutionLogEntry): string {
  return (entry.user_email || entry.user_id || '').trim() || '?';
}

function logUserTooltip(entry: ExecutionLogEntry): string {
  return entry.user_email?.trim() || entry.user_id?.trim() || 'Unknown';
}

function parseLogTimestampMs(iso: string): number | null {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function getLogEntryDescription(entry: ExecutionLogEntry): string {
  const d = entry.details as Record<string, unknown> | undefined;
  if (entry.event_type === 'activate') {
    return d?.scenario_name ? `Scenario "${String(d.scenario_name)}" activated` : 'Scenario activated';
  }
  if (entry.event_type === 'start') return 'Execution started';
  if (entry.event_type === 'start_failed' && d?.reason) return `Start attempt failed: ${String(d.reason)}`;
  if (entry.event_type === 'start_failed') return 'Start attempt failed';
  if (entry.event_type === 'pause') return 'Attack paused';
  if (entry.event_type === 'termination' && d) {
    if (d.reason) return String(d.reason);
    if (d.status) return `Status: ${d.status}`;
  }
  if (!d) return '';
  if (entry.event_type === 'scale') {
    const parts: string[] = [];
    const requested = d.requested_amount != null ? Number(d.requested_amount) : null;
    const created = d.bots_created != null ? Number(d.bots_created) : null;
    if (requested != null && created != null && created < requested)
      parts.push(`Requested ${requested}, created ${created}`);
    if (d.threads != null) parts.push(`threads: ${d.threads}`);
    if (d.bots_created != null && Number(d.bots_created) > 0) parts.push(`+${d.bots_created} created`);
    if (d.bots_started != null && Number(d.bots_started) > 0) parts.push(`+${d.bots_started} started`);
    if (d.bots_shutdown != null && Number(d.bots_shutdown) > 0) parts.push(`−${d.bots_shutdown} shutdown`);
    if (d.regions != null && Array.isArray(d.regions) && (d.regions as string[]).length > 0)
      parts.push(`regions: ${(d.regions as string[]).join(', ')}`);
    return parts.join(' · ') || '—';
  }
  if (entry.event_type === 'config_change') {
    if (d.updated_fields != null) {
      const uf = d.updated_fields;
      return `Fields: ${Array.isArray(uf) ? (uf as string[]).join(', ') : String(uf)}`;
    }
    if (d.threads_new != null || d.threads_previous != null)
      return `threads: ${d.threads_previous ?? '—'} → ${d.threads_new ?? '—'}`;
    return 'Configuration updated';
  }
  return '';
}

function executionMetricsRange(detail: Execution): { startSec: number; endSec: number } | null {
  if (!detail.started_at) return null;
  const startMs = new Date(detail.started_at).getTime();
  if (Number.isNaN(startMs)) return null;
  const startSec = Math.floor(startMs / 1000);
  let endSec: number;
  if (detail.completed_at) {
    const endMs = new Date(detail.completed_at).getTime();
    endSec = Number.isNaN(endMs) ? Math.floor(Date.now() / 1000) : Math.floor(endMs / 1000);
  } else {
    endSec = Math.floor(Date.now() / 1000);
  }
  if (endSec <= startSec) endSec = startSec + 60;
  return { startSec, endSec };
}

function getStatusColor(status: string) {
  const key = status as keyof typeof ExecutionStatusColors;
  return (ExecutionStatusColors[key] ?? 'default') as
    | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

interface RunbookExecutionDetailProps {
  execution: Execution;
  scenarioName: string;
  onExecutionUpdated?: (updated: Execution) => void;
}

const RunbookExecutionDetail: React.FC<RunbookExecutionDetailProps> = ({
  execution: initialExecution,
  scenarioName,
  onExecutionUpdated,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { timezone } = useTimezone();
  const { showNotification } = useNotification();
  const { selectedTenant } = useTenant();

  const [execution, setExecution] = useState<Execution>(initialExecution);
  const [impactBotsEdit, setImpactBotsEdit] = useState<string>(
    initialExecution.impact_bots != null ? String(initialExecution.impact_bots) : ''
  );
  const [savingImpactBots, setSavingImpactBots] = useState(false);
  const [configSnapshotExpanded, setConfigSnapshotExpanded] = useState(false);

  /**
   * One reference time shared by every metrics chart: hovering a log row moves it,
   * clicking a log row or a point on any chart locks it there.
   */
  const cursor = useExecutionCursorSync();

  const [splitPct, setSplitPct] = useState(readStoredSplitPct);
  const [showSummary, setShowSummary] = useState(() => readStoredBool(SHOW_SUMMARY_KEY, true));
  const [showLog, setShowLog] = useState(() => readStoredBool(SHOW_LOG_KEY, true));
  const [showMetrics, setShowMetrics] = useState(() => readStoredBool(SHOW_METRICS_KEY, true));
  const splitContainerRef = useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setExecution(initialExecution);
    setImpactBotsEdit(initialExecution.impact_bots != null ? String(initialExecution.impact_bots) : '');
    cursor.clear();
  }, [initialExecution.id, initialExecution, cursor.clear]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    try { localStorage.setItem(SPLIT_PCT_KEY, String(splitPct)); } catch { /* ignore */ }
  }, [splitPct]);
  React.useEffect(() => {
    try { localStorage.setItem(SHOW_SUMMARY_KEY, showSummary ? '1' : '0'); } catch { /* ignore */ }
  }, [showSummary]);
  React.useEffect(() => {
    try { localStorage.setItem(SHOW_LOG_KEY, showLog ? '1' : '0'); } catch { /* ignore */ }
  }, [showLog]);
  React.useEffect(() => {
    try { localStorage.setItem(SHOW_METRICS_KEY, showMetrics ? '1' : '0'); } catch { /* ignore */ }
  }, [showMetrics]);

  React.useEffect(() => {
    if (!showLog) cursor.clear();
  }, [showLog, cursor.clear]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLog = useCallback(() => {
    setShowLog((l) => {
      if (!l) return true;
      setShowMetrics((m) => (!m ? true : m));
      return false;
    });
  }, []);

  const toggleMetrics = useCallback(() => {
    setShowMetrics((m) => {
      if (!m) return true;
      setShowLog((l) => (!l ? true : l));
      return false;
    });
  }, []);

  const onDividerDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const el = splitContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = Math.max(1, rect.width - DIVIDER_PX);
      const startX = e.clientX;
      const startPct = splitPct;
      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        setSplitPct(Math.min(80, Math.max(20, startPct + (dx / total) * 100)));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [splitPct]
  );

  const handleSaveImpactBots = useCallback(async () => {
    if (!execution?.id) return;
    const num = impactBotsEdit.trim() === '' ? null : parseInt(impactBotsEdit.trim(), 10);
    if (num !== null && (Number.isNaN(num) || num < 0)) {
      showNotification('Please enter a non-negative number.', 'error');
      return;
    }
    setSavingImpactBots(true);
    try {
      const updated = await api.updateExecutionImpactBots(execution.id, num);
      setExecution(updated);
      onExecutionUpdated?.(updated);
      showNotification('Impact Bots updated.', 'success');
    } catch (err) {
      console.error('Failed to update impact bots:', err);
      showNotification('Failed to update Impact Bots.', 'error');
    } finally {
      setSavingImpactBots(false);
    }
  }, [execution, impactBotsEdit, showNotification, onExecutionUpdated]);

  const metricsRange = executionMetricsRange(execution);
  const dur = durationMs(execution.started_at, execution.completed_at);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Summary card */}
      {showSummary && (
        <Card
          sx={{
            mb: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(12px)',
            border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.2)}`,
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)}, transparent)`,
            },
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Tooltip title="Hide summary" arrow>
                <IconButton
                  onClick={() => setShowSummary(false)}
                  aria-label="Hide summary panel"
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18),
                    borderRadius: '10px',
                    p: 1,
                    border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.3)}`,
                    color: theme.palette.primary.main,
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.28) },
                  }}
                >
                  <NotesIcon sx={{ fontSize: '1.15rem' }} />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: 'text.primary' }}>
                  Summary
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'flex-start' },
                gap: { xs: 2.5, md: 3 },
              }}
            >
              {/* Left: status + timeline */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, rowGap: 1, minWidth: 0 }}>
                <Chip
                  label={execution.status}
                  size="small"
                  color={getStatusColor(execution.status)}
                  variant="filled"
                  aria-label={`Execution status: ${execution.status}`}
                />
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, borderColor: alpha(theme.palette.divider, 0.9) }} />
                <Tooltip title="Started">
                  <Typography component="span" variant="body2" sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem', color: 'text.primary' }}>
                    {formatUiDateTime(execution.started_at, timezone) || '—'}
                  </Typography>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, borderColor: alpha(theme.palette.divider, 0.9) }} />
                <Tooltip title="Completed">
                  <Typography component="span" variant="body2" sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem', color: 'text.primary' }}>
                    {formatUiDateTime(execution.completed_at, timezone) || '—'}
                  </Typography>
                </Tooltip>
                {dur != null && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, borderColor: alpha(theme.palette.divider, 0.9) }} />
                    <Typography component="span" variant="body2" sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                      {formatDuration(dur)}
                    </Typography>
                  </>
                )}
              </Box>

              {/* Right: impact bots + config snapshot */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  alignItems: { xs: 'stretch', md: 'flex-end' },
                  flexShrink: 0,
                  minWidth: { md: 280 },
                  maxWidth: { md: 420 },
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"JetBrains Mono", monospace', display: 'block', mb: 0.75 }}>
                    Impact bots
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
                    <TextField
                      type="number"
                      size="small"
                      value={impactBotsEdit}
                      onChange={(e) => setImpactBotsEdit(e.target.value)}
                      placeholder="—"
                      inputProps={{ min: 0, step: 1 }}
                      sx={{ width: 120 }}
                    />
                    <Button size="small" variant="contained" onClick={handleSaveImpactBots} disabled={savingImpactBots}>
                      {savingImpactBots ? 'Saving…' : 'Update'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, textAlign: { md: 'right' } }}>
                    Updates are recorded in the Audit Log.
                  </Typography>
                </Box>
                {hasResourceCaps(execution.scenario) && (
                  <Box sx={{ width: '100%', textAlign: { md: 'right' } }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: '"JetBrains Mono", monospace', display: 'block', mb: 0.5 }}
                    >
                      Resource caps (per bot)
                    </Typography>
                    {describeResourceCaps(execution.scenario, execution.impact_bots ?? undefined).map((line) => (
                      <Typography
                        key={line}
                        variant="caption"
                        display="block"
                        sx={{ fontFamily: '"Space Mono", monospace', color: theme.palette.warning.main }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                )}
                <Box sx={{ width: '100%' }}>
                  <Button
                    size="small"
                    onClick={() => setConfigSnapshotExpanded((e) => !e)}
                    sx={{ textTransform: 'none', alignSelf: { md: 'flex-end' }, display: 'block', ml: { md: 'auto' } }}
                  >
                    Configuration (snapshot) {configSnapshotExpanded ? '(collapse)' : '(expand)'}
                  </Button>
                  {configSnapshotExpanded && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mt: 0.75,
                        maxHeight: 200,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(execution.configuration ?? {}, null, 2)}
                      </pre>
                    </Paper>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Panel visibility controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!showSummary && (
            <Tooltip title="Show summary panel" arrow>
              <IconButton
                size="small"
                onClick={() => setShowSummary(true)}
                aria-label="Show summary panel"
                sx={{ border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.4)}`, color: theme.palette.primary.main }}
              >
                <NotesIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!showLog && (
            <Tooltip title="Show log panel" arrow>
              <IconButton
                size="small"
                onClick={toggleLog}
                aria-label="Show log panel"
                sx={{ border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.4)}`, color: theme.palette.primary.main }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!showMetrics && (
            <Tooltip title="Show metrics panel" arrow>
              <IconButton
                size="small"
                onClick={toggleMetrics}
                aria-label="Show metrics panel"
                sx={{ border: `1px solid ${alpha(theme.palette.secondary.main, isDark ? 0.35 : 0.4)}`, color: theme.palette.secondary.main }}
              >
                <TimelineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {showLog && showMetrics && (
          <Typography variant="caption" color="text.disabled" sx={{ display: { xs: 'none', md: 'inline' } }}>
            Drag the handle between columns to resize
          </Typography>
        )}
      </Box>

      {/* Log + Metrics split pane */}
      <Box
        ref={splitContainerRef}
        sx={{
          width: '100%',
          display: { xs: 'flex', md: 'grid' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          gridTemplateColumns: {
            md:
              !showLog || !showMetrics
                ? 'minmax(0, 1fr)'
                : `${splitPct}% ${DIVIDER_PX}px minmax(0, 1fr)`,
          },
          alignItems: 'stretch',
          minHeight: { md: 360 },
        }}
      >
        {showLog && (
          <Box sx={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Card
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minWidth: 0,
                width: '100%',
                minHeight: { xs: 280, md: 0 },
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(12px)',
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.2)}`,
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)}, transparent)`,
                },
              }}
            >
              <CardContent
                sx={{ flex: { xs: 'none', md: 1 }, minHeight: { xs: 240, md: 0 }, display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 2 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexShrink: 0 }}>
                  <Tooltip title="Hide log panel" arrow>
                    <IconButton
                      onClick={toggleLog}
                      aria-label="Hide log panel"
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18),
                        borderRadius: '10px',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.3)}`,
                        color: theme.palette.primary.main,
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.28) },
                      }}
                    >
                      <HistoryIcon sx={{ fontSize: '1.15rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Box>
                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: 'text.primary' }}>
                      Execution log
                    </Typography>
                    <Typography sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.75rem', color: 'text.secondary', mt: 0.25, opacity: 0.85 }}>
                      Event timeline for this run
                    </Typography>
                  </Box>
                </Box>

                <TableContainer
                  onMouseLeave={() => { if (!cursor.isLocked) cursor.hoverLogRow(null); }}
                  sx={{
                    flex: { xs: 'none', md: 1 },
                    minHeight: { xs: 200, md: 0 },
                    width: '100%',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18)}`,
                    backgroundColor: alpha(theme.palette.background.default, isDark ? 0.25 : 0.04),
                  }}
                >
                  <Table size="small" stickyHeader sx={{ tableLayout: 'auto', width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            width: '1%',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'bottom',
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: 'text.secondary',
                            borderBottom: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.22)}`,
                            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.92 : 0.97),
                          }}
                        >
                          Timestamp
                        </TableCell>
                        <TableCell
                          sx={{
                            width: '100%',
                            minWidth: 0,
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: 'text.secondary',
                            borderBottom: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.22)}`,
                            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.92 : 0.97),
                          }}
                        >
                          Description
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!execution.execution_log || execution.execution_log.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.85rem', color: 'text.secondary' }}>
                              No events recorded.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (execution.execution_log as ExecutionLogEntry[]).map((entry, idx) => {
                          const uKey = logUserKey(entry);
                          const av = userAvatarColors(uKey, isDark);
                          const fullWhen = formatUiDateTime(entry.timestamp, timezone);
                          const shortWhen = formatUiTimeWithSeconds(entry.timestamp, timezone);
                          return (
                            <TableRow
                              key={idx}
                              hover
                              selected={cursor.selectedLogRowIndex === idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                const ms = parseLogTimestampMs(entry.timestamp);
                                if (ms == null) return;
                                cursor.selectLogRow(idx, ms);
                              }}
                              onMouseEnter={() => {
                                if (cursor.isLocked) return;
                                const ms = parseLogTimestampMs(entry.timestamp);
                                if (ms != null) cursor.hoverLogRow(ms);
                              }}
                              sx={{
                                '&:last-child td': { borderBottom: 0 },
                                '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.14 : 0.1) },
                                '&.Mui-selected:hover': { backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.14) },
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.06 : 0.05) },
                              }}
                            >
                              <TableCell
                                sx={{
                                  verticalAlign: 'top',
                                  borderColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.1),
                                  width: '1%',
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, width: 'max-content' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                                    <Tooltip title={fullWhen} placement="top" arrow enterDelay={400}>
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontFamily: '"Space Mono", monospace',
                                          fontSize: '0.85rem',
                                          fontWeight: 600,
                                          color: 'text.primary',
                                          cursor: 'default',
                                          borderBottom: `1px dotted ${alpha(theme.palette.text.secondary, 0.45)}`,
                                        }}
                                      >
                                        {shortWhen}
                                      </Typography>
                                    </Tooltip>
                                    <Tooltip title={logUserTooltip(entry)} placement="top" arrow enterDelay={400}>
                                      <Avatar
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          fontSize: '0.8rem',
                                          fontFamily: '"JetBrains Mono", monospace',
                                          fontWeight: 700,
                                          bgcolor: av.bg,
                                          color: av.fg,
                                          border: `1px solid ${alpha(theme.palette.common.white, isDark ? 0.12 : 0.2)}`,
                                          cursor: 'default',
                                        }}
                                        aria-label={logUserTooltip(entry)}
                                      >
                                        {logUserInitial(entry)}
                                      </Avatar>
                                    </Tooltip>
                                  </Box>
                                  <Chip
                                    label={formatLogEventType(entry.event_type)}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontFamily: '"JetBrains Mono", monospace',
                                      fontSize: '0.6rem',
                                      height: 'auto',
                                      maxWidth: 'none',
                                      alignSelf: 'flex-start',
                                      borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.4),
                                      color: theme.palette.primary.main,
                                      '& .MuiChip-label': { whiteSpace: 'nowrap', lineHeight: 1.25, py: 0.4, display: 'block' },
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  verticalAlign: 'top',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                  borderColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.1),
                                  minWidth: 0,
                                }}
                              >
                                <Typography sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.875rem', lineHeight: 1.45, color: 'text.primary' }}>
                                  {getLogEntryDescription(entry) || '—'}
                                </Typography>
                                {(entry.provider_name || entry.region_name) && (
                                  <Typography
                                    component="div"
                                    sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.7rem', color: 'text.secondary', mt: 0.75, opacity: 0.9 }}
                                  >
                                    {[entry.provider_name, entry.region_name].filter(Boolean).join(' · ')}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}

        {showLog && showMetrics && (
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize execution log and metrics"
            onMouseDown={onDividerDown}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'col-resize',
              userSelect: 'none',
              borderRadius: 1,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08) },
            }}
          >
            <DragIndicatorIcon sx={{ color: 'text.secondary', fontSize: '1rem', transform: 'rotate(90deg)' }} />
          </Box>
        )}

        {showMetrics && (
          <Box sx={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {!metricsRange ? (
              <Alert severity="info">
                Metrics need a recorded start time for this execution. Prometheus charts are unavailable until started_at is set.
              </Alert>
            ) : (
              <MetricsPanel
                isCollapsed={false}
                onToggle={() => {}}
                tenantId={selectedTenant?.tenant_id ?? null}
                scenarioId={execution.scenario_id}
                scenarioName={execution.scenario?.name ?? scenarioName}
                attackClass={execution.scenario?.attack_type?.attack_class}
                fixedRangeSec={metricsRange}
                variant="embedded"
                configurablePanels
                syncReferenceTimeMs={cursor.activeReferenceMs}
                onSelectReferenceTime={cursor.selectChartPoint}
                referenceLocked={cursor.isLocked}
                onEmbeddedAvatarClick={toggleMetrics}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RunbookExecutionDetail;
